import { transformSearch } from "@/api/helper.ts";
import type { CounselFormSchema } from "@/features/case-evaluation/types/counsel";
import { queryClient } from "@/query-client.ts";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import caseRecordService from "../services/case-records-service";

export const caseRecordSearchQuery = z.object({
	search: z.string().optional(),
	status: z.string().optional(),
	stage: z.string().optional(),
	per_page: z.number().optional(),
	item_per_page: z.number().optional(),
	page: z.number().nullable().optional(),
});

export const useRecordUserLeadMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.updateIsLead,
		meta: {
			successMessage: "Lead counsel updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const caseRecordsQueryOptions = (search?: z.infer<typeof caseRecordSearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_records", search],
		queryFn: () => caseRecordService.list(transformSearch(search, ["status", "search", "stage"])),
		...options,
	});
};

export const caseRecordSearchQueryOptions = (search?: z.infer<typeof caseRecordSearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_record_search", search],
		queryFn: () => caseRecordService.lisyBySearch(transformSearch(search, ["search"])),
		...options,
	});
};

export const caseRecordQueryOptions = (caseRecordId: string) => {
	return queryOptions({
		queryKey: ["case_records", caseRecordId],
		queryFn: () => caseRecordService.findById(caseRecordId),
	});
};

export const useCaseRecordOverviewMutation = (caseRecordId?: string, data?: Record<string, unknown>) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId, data],
		mutationFn: ({ caseRecordId, data }: { caseRecordId: string; data: Record<string, unknown> }) => caseRecordService.updateCaseOverview(caseRecordId, data),
		meta: {
			successMessage: "Case Record updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useRemoveCounselMutation = (caseRecordId?: string, data?: Record<string, unknown>) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId, data],
		mutationFn: ({ caseRecordId, data }: { caseRecordId: string; data: Record<string, unknown> }) => caseRecordService.removeCounsel(caseRecordId, data),
		meta: {
			successMessage: "Counsel has been removed successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useRegenerateKeyCodeMutation = (caseRecordId?: string, data?: Record<string, unknown>) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId, data],
		mutationFn: ({ caseRecordId, data }: { caseRecordId: string; data: Record<string, unknown> }) => caseRecordService.regenerateKeyCode(caseRecordId, data),
		meta: {
			successMessage: "Key code has been regenerated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useReplaceCounselMutation = (caseRecordId?: string, data?: Record<string, unknown>) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId, data],
		mutationFn: ({ caseRecordId, data }: { caseRecordId: string; data: Record<string, unknown> }) => caseRecordService.replaceCounsel(caseRecordId, data),
		meta: {
			successMessage: "Counsel has been replaced successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.upsert,
		meta: {
			successMessage: "Case Record saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordDefendantMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.updateDefendantById,
		meta: {
			successMessage: "Defendant/s updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordDefendantCounselMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.updateDefendantCounselById,
		meta: {
			successMessage: "Defendant updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordStatusMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.updateStatusById,
		meta: {
			successMessage: "Case Record updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordPostAsMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.updatePostAsById,
		meta: {
			successMessage: "Case Record updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordPaymentProofMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.upsertById,
		meta: {
			successMessage: "File updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordCaseComplaintMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.uploadComplaint,
		meta: {
			successMessage: "File updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordAnnexMutation = (caseRecordId?: string) => {
	return useMutation({
		mutationKey: ["case_records", caseRecordId],
		mutationFn: caseRecordService.uploadAnnex,
		meta: {
			successMessage: "File updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseRecordAnnexDeleteMutation = (caseRecordId?: string, mediaId?: string) => {
	return useMutation({
		mutationKey: ["case_records", "delete"],
		mutationFn: ({ caseRecordId, mediaId }: { caseRecordId: string; mediaId: string }) => caseRecordService.deleteAnnex(caseRecordId, mediaId),
		meta: {
			successMessage: "File removed successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["case_records", caseRecordId, mediaId],
			});
			queryClient.invalidateQueries({
				queryKey: ["case_records"],
			});
		},
	});
};

export const useCaseRecordMediaDeleteMutation = (caseRecordId?: string, mediaId?: string) => {
	return useMutation({
		mutationKey: ["case_records", "delete"],
		mutationFn: ({ caseRecordId, mediaId }: { caseRecordId: string; mediaId: string }) => caseRecordService.deleteMedia(caseRecordId, mediaId),
		meta: {
			successMessage: "File removed successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["case_records", caseRecordId, mediaId],
			});
			queryClient.invalidateQueries({
				queryKey: ["case_records"],
			});
		},
	});
};

export const useCaseRecordDeleteMutation = () => {
	return useMutation({
		mutationKey: ["case_records", "delete"],
		mutationFn: caseRecordService.deleteById,
		meta: {
			successMessage: "Case Record archived successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["case_records"],
			});
		},
	});
};

export const useCaseInvitePreviewMutation = () => {
	return useMutation({
		mutationKey: ["case_invite_preview"],
		mutationFn: caseRecordService.preview,
	});
};

export const useCaseCounselMutation = () => {
	return useMutation({
		mutationFn: (data: CounselFormSchema) => caseRecordService.custom(data),
		meta: {
			successMessage: "Counsel saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_counsels"] });
		},
	});
};

export const useAddDetailsDefendantMutation = () => {
	return useMutation({
		mutationFn: (data: CounselFormSchema) => caseRecordService.addDefendant(data),
		meta: {
			successMessage: "Defendant saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_counsels"] });
		},
	});
};

export const useEditAddressMutation = () => {
	return useMutation({
		mutationFn: (data: CounselFormSchema) => caseRecordService.editAddress(data),
		meta: {
			successMessage: "Address updated successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
		},
	});
};

export const useEditNameMutation = () => {
	return useMutation({
		mutationFn: (data: CounselFormSchema) => caseRecordService.editName(data),
		meta: {
			successMessage: "Name updated successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
		},
	});
};

export const useSetBranchMutation = () => {
	return useMutation({
		mutationFn: (data: CounselFormSchema) => caseRecordService.setBranch(data),
		meta: {
			successMessage: "Branch updated successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
		},
	});
};

export const useUpdateTsnMutation = () => {
	return useMutation({
		mutationFn: (data: any) => caseRecordService.updateTsn(data),
		meta: {
			successMessage: "TSN updated successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};

export const useUpdateSheriffReportMutation = () => {
	return useMutation({
		mutationFn: (data: any) => caseRecordService.updateSheriffReport(data),
		meta: {
			successMessage: "TSN updated successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};

export const useCasePartyMutation = () => {
	return useMutation({
		mutationFn: (data: CounselFormSchema) => caseRecordService.partyComplaint(data),
		meta: {
			successMessage: "Party Complaint saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
		},
	});
};
export const useCaseRolloGenerateMutation = (caseId: string) => {
	return useMutation({
		mutationFn: () => caseRecordService.generateRollo(caseId),
		meta: {
			successMessage: "Generated Rollo",
		},
		onSuccess: () => {
			// do nothing
		},
	});
};
